import { ClarificationResponse } from '../client/SmartAssistantBackendClient';
import { getClarificationFromPayload } from './clarification';
import { handleFinalEvent } from './handleFinalEvent';
import { handleSequencedPayload } from './handleSequencedPayload';
import { normalizeSeqPayload } from './normalizeSeqPayload';

export const createIncomingHandler = (params: {
  unwrapMsgEnvelope: (payload: any) => any;
  mergeMessages: (current: any[], payload: any, eventName?: string) => any[];
  getChatBuffer: (id: string, fallback: any[]) => any[];
  setChatBuffer: (id: string, messages: any[]) => void;
  activeChatIdRef: React.MutableRefObject<string | null>;
  setHistory: React.Dispatch<React.SetStateAction<any[]>>;
  setClarificationsToSend: React.Dispatch<React.SetStateAction<ClarificationResponse | undefined>>;
  sequencedMessagesRef: React.MutableRefObject<Map<string, { nextSeq: number; buffer: Map<number, string>; lastSeq: number }>>;
  sequencedFlushTimersRef: React.MutableRefObject<Map<string, number>>;
  lastReceivedSeqRef: React.MutableRefObject<Map<string, number>>;
  missingSeqRef: React.MutableRefObject<Map<string, number>>;
  missingSeqRetriesRef: React.MutableRefObject<Map<string, number>>;
  missingSeqTimersRef: React.MutableRefObject<Map<string, number>>;
  requestReplay: (targetChatId: string, lastSeq: number) => void;
  flushSequencedBuffer: (targetChatId: string, state: { nextSeq: number; buffer: Map<number, string>; lastSeq: number }) => void;
}) => {
  return (chatKey: string, payload: any, eventName?: string) => {
    let parsedPayload = payload;
    if (typeof payload === 'string') {
      try {
        parsedPayload = JSON.parse(payload);
      } catch {
        parsedPayload = payload;
      }
    }
    parsedPayload = params.unwrapMsgEnvelope(parsedPayload);

    const clarification = getClarificationFromPayload(parsedPayload);
    if (clarification) {
      params.setClarificationsToSend(ClarificationResponse.fromJS(clarification));
    }

    const handledFinal = handleFinalEvent({
      chatKey,
      parsedPayload,
      eventName,
      sequencedMessagesRef: params.sequencedMessagesRef,
      sequencedFlushTimersRef: params.sequencedFlushTimersRef,
      flushSequencedBuffer: params.flushSequencedBuffer,
      getChatBuffer: params.getChatBuffer,
      mergeMessages: params.mergeMessages,
      setChatBuffer: params.setChatBuffer,
      activeChatIdRef: params.activeChatIdRef,
      setHistory: params.setHistory,
    });
    if (handledFinal) return;

    const sequencedPayload = normalizeSeqPayload(parsedPayload, chatKey);
    if (sequencedPayload) {
      const handled = handleSequencedPayload({
        targetChatId: sequencedPayload.targetChatId,
        seqValue: sequencedPayload.seq,
        content: sequencedPayload.content,
        getChatBuffer: params.getChatBuffer,
        mergeMessages: params.mergeMessages,
        setChatBuffer: params.setChatBuffer,
        activeChatIdRef: params.activeChatIdRef,
        setHistory: params.setHistory,
        sequencedMessagesRef: params.sequencedMessagesRef,
        sequencedFlushTimersRef: params.sequencedFlushTimersRef,
        lastReceivedSeqRef: params.lastReceivedSeqRef,
        missingSeqRef: params.missingSeqRef,
        missingSeqRetriesRef: params.missingSeqRetriesRef,
        missingSeqTimersRef: params.missingSeqTimersRef,
        requestReplay: params.requestReplay,
        flushSequencedBuffer: params.flushSequencedBuffer,
      });
      if (handled) return;
    }

    const baseMessages = params.getChatBuffer(chatKey, []);
    const merged = params.mergeMessages(baseMessages, payload, eventName);
    params.setChatBuffer(chatKey, [...merged]);
    if (params.activeChatIdRef.current === chatKey) {
      params.setHistory(merged);
    }
  };
};
